import type { ScreenBase } from "@frui.ts/screens/dist";
import ListViewModel from "viewModels/listViewModel";
import type { interfaces } from "inversify";
import type User from "entities/user";
import { computed } from "mobx";
import RoleUserRepository from "repositories/roleUserRepository";
import EventBus from "services/eventBus";
import LocalizationService from "services/localizationService";
import NotificationService from "services/notificationService";
import AppRouter from "services/router";
import { Modules } from "config";
import SecurityService from "services/securityService";
import { bound } from "@frui.ts/helpers";
import ConfirmationService from "services/confirmationService";

export default class RoleUsersListViewModel extends ListViewModel<User, ScreenBase, ScreenBase, any> {
  readonly modelName = "user";
  readonly idProperty = "id";

  @computed
  get name() {
    return this.translate("name");
  }

  constructor(
    protected itemId: string,
    protected repository: RoleUserRepository,
    protected localizationService: LocalizationService,
    protected appRouter: AppRouter,
    protected notificationService: NotificationService,
    protected securityService: SecurityService,
    protected confirmationService: ConfirmationService,
    protected eventBus: EventBus
  ) {
    super();
  }

  protected onActivate(): Promise<void> {
    this.loadData();
    return super.onActivate();
  }

  @bound
  onDeleteClick() {
    return async (item: User) => {
      const message = this.translate("deleteConfirmation").replace("%fullName%", `${item.firstName} ${item.lastName}`);
      const confirmed = await this.confirmationService.deleteConfirmation(message);

      if (confirmed) {
        try {
          await this.delete(item);
          this.onDeleted(item);
        } catch (e) {}
      }
    };
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.users, `roleUsers.${code}`, size);
  }

  protected fetchData() {
    const { limit, offset } = this.pagingFilter;
    return this.repository.search(this.itemId, { offset, limit });
  }

  @computed
  get isBusy() {
    return this.busyWatcher.isBusy;
  }

  static Factory({ container }: interfaces.Context) {
    return (itemId: string) => {
      return new RoleUsersListViewModel(
        itemId,
        container.get(RoleUserRepository),
        container.get(LocalizationService),
        container.get(AppRouter),
        container.get(NotificationService),
        container.get(SecurityService),
        container.get(ConfirmationService),
        container.get(EventBus)
      );
    };
  }
}
